// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button, EmptyState, Icon, Pagination, usePagination } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useAccountUserGroupsQuery } from 'hooks/queries/useAccountUserGroupsQuery'
import { useAccountUsersQuery } from 'hooks/queries/useAccountUsersQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { AccountUser } from 'types/accounts'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { TeamMemberDelete } from './components/TeamMemberDelete'
import { TeamMemberEditor } from './components/TeamMemberEditor'
import { TeamMemberTable } from './components/TeamMemberTable'

const DEFAULT_NUMBER_OF_ROWS = '10'

const TeamMembers = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const permissions = useMappedRoles()?.manageTeamMembers
  const manageApproverPermissions = useMappedRoles()?.manageTeamMemberApprovers
  const canEdit = permissions?.canEdit
  const canManageApprover = manageApproverPermissions?.canEdit
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = usePagination()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_TEAM_MEMBERS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AccountUser | null>(null)
  const [selectedUserToDelete, setSelectedUserToDelete] = useState<AccountUser | null>(null)

  const {
    profile: { emailAddress },
  } = useProfileContext()

  const {
    data: { results: accountUsers, paging } = {},
    isFetching: isFetchingAccountUsers,
    refetch: refetchAccountUsers,
    isError,
  } = useAccountUsersQuery({
    offset: page * rowsPerPage,
    max: rowsPerPage,
  })

  const showPagination = useMemo(() => paging?.total > MIN_PAGINATION_SIZE, [paging?.total])

  const { data: accountUserGroups, isFetching: isFetchingAccountUserGroups } =
    useAccountUserGroupsQuery()

  const isLoading = useMemo(
    () => isFetchingAccountUserGroups || isFetchingAccountUsers,
    [isFetchingAccountUserGroups, isFetchingAccountUsers]
  )

  const invalidateAccountUsers = () => {
    refetchAccountUsers()
    queryClient.invalidateQueries({ queryKey: reactQueryKeys.accountUserGroups() })
  }

  const onEditUserClick = (user: AccountUser) => {
    setSelectedUser(user)
    setEditModalOpen(true)
  }

  const onDeleteUserClick = (user: AccountUser) => {
    setSelectedUserToDelete(user)
    setDeleteModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    if (!!selectedUser) {
      setSelectedUser(null)
    }
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    if (!!selectedUserToDelete) {
      setSelectedUserToDelete(null)
    }
  }

  return (
    <>
      <PageHeader
        description={canEdit ? t('teamMemberSubParagraphEdit') : t('teamMemberSubParagraphView')}
        renderRightSection={
          canEdit && (
            <Button onClick={() => setEditModalOpen(true)} iconElement={<Icon name="Plus" />}>
              {t('addTeamMember')}
            </Button>
          )
        }
      />
      {!isFetchingAccountUsers && isError && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
        />
      )}

      {/* TODO: implement search - https://coindirectworld.atlassian.net/browse/MXE-1210 */}

      {!isFetchingAccountUsers && !isError && accountUsers && (
        <TeamMemberTable
          accountUsers={accountUsers}
          currentEmailAddress={emailAddress}
          onEditUserClick={onEditUserClick}
          onDeleteUserClick={onDeleteUserClick}
          invalidateAccountUsers={invalidateAccountUsers}
          isFetchingAccountUsers={isLoading}
          defaultNumberOfRows={DEFAULT_NUMBER_OF_ROWS}
          canEdit={canEdit}
          canManageApprover={canManageApprover}
        />
      )}

      {showPagination && !isError && (
        <Pagination
          testId="team-member-pagination"
          page={page}
          rowsPerPage={rowsPerPage}
          total={paging?.total || 0}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      )}

      {deleteModalOpen && (
        <TeamMemberDelete
          userToDelete={selectedUserToDelete}
          onDeleteCallback={invalidateAccountUsers}
          handleCloseModal={handleCloseDeleteModal}
        />
      )}

      {editModalOpen && (
        <TeamMemberEditor
          action={selectedUser ? 'EDIT' : 'CREATE'}
          accountUser={selectedUser}
          userGroups={accountUserGroups}
          canManageApprover={canManageApprover}
          handleCloseModal={handleCloseEditModal}
          getValues={invalidateAccountUsers}
        />
      )}
    </>
  )
}

export default TeamMembers
